import { useEffect, useRef, useState } from 'react';

/**
 * Miniature delle immagini nel vault — decifrate NEL BROWSER, mai sul server.
 *
 * IL VINCOLO CHE GOVERNA TUTTO IL FILE.
 * Il server non puo' generare miniature: non ha la chiave, e se ce l'avesse
 * la promessa zero-knowledge sarebbe finita. Quindi ogni miniatura costa un
 * download del file intero piu' una decifratura in memoria. E' caro, ed e' il
 * motivo di ognuna delle regole qui sotto.
 *
 * 1. SI DECIFRA SOLO CIO' CHE E' VISIBILE.
 *    Un IntersectionObserver fa partire il lavoro quando la riga entra nello
 *    schermo. Una cartella con 300 foto non scarica 300 file: ne scarica
 *    quelli che stai guardando. Chi scorre veloce non ne paga nessuno,
 *    perche' l'osservatore annulla prima ancora di iniziare.
 *
 * 2. NON PIU' DI DIECI IMMAGINI IN MEMORIA.
 *    Una cache condivisa fra tutte le righe, con espulsione della piu'
 *    vecchia. Ogni blob URL espulso viene revocato: senza revoca il browser
 *    tiene i byte decifrati finche' la scheda e' aperta, ed e' esattamente
 *    quello che non vogliamo lasciare in giro.
 *
 * 3. NIENTE VA SU DISCO.
 *    Solo blob URL in memoria. Mai localStorage, mai IndexedDB, mai la Cache
 *    API: sono tutti persistenti, e ci finirebbe dentro il file in chiaro.
 *    Il service worker ignora questi URL perche' blob: non passa da fetch.
 *
 * 4. SOPRA UNA CERTA DIMENSIONE NON SI PROVA NEMMENO.
 *    La decifratura tiene in memoria il file cifrato E il risultato: il picco
 *    e' circa il doppio del file. Su un telefono una foto da 60 MB fa chiudere
 *    la scheda al sistema operativo, e l'utente vede l'app "crashare" senza
 *    capire perche'. Sopra la soglia si mostra l'icona normale, che e' un
 *    ripiego onesto e istantaneo.
 */

// jpg/jpeg/png/webp/gif e nient'altro. SVG e' deliberatamente escluso: e'
// codice, non pixel, e un SVG puo' contenere script — non e' materiale da
// dare in pasto a <img> senza pensarci. BMP e' escluso perche' non compresso:
// una miniatura da 30 MB per mostrare 40 pixel non ha senso.
const TIPI_MINIATURA = new Set(['jpg', 'jpeg', 'png', 'webp', 'gif']);

// Soglie oltre le quali si mostra l'icona invece della miniatura.
// Il picco di memoria e' circa il doppio di questi numeri.
// Desktop 25 MB (picco ~50) e mobile 8 MB (picco ~16): una foto di un
// telefono moderno sta fra 2 e 6 MB, quindi il caso normale passa sempre,
// e restano fuori le scansioni, i RAW e le immagini da fotocamera reflex.
const LIMITE_DESKTOP = 25 * 1024 * 1024;
const LIMITE_MOBILE = 8 * 1024 * 1024;

const MAX_IN_MEMORIA = 10;

export function limiteMiniatura() {
  // Non solo la larghezza: un telefono in orizzontale e' largo, ma ha la
  // memoria di un telefono. Se il browser espone deviceMemory la si usa,
  // altrimenti si guarda il puntatore grossolano (dito) e la larghezza.
  try {
    const poca = navigator.deviceMemory && navigator.deviceMemory <= 4;
    const tocco = window.matchMedia?.('(pointer: coarse)').matches;
    const stretto = window.innerWidth <= 900;
    if (poca || (tocco && stretto)) return LIMITE_MOBILE;
  } catch { /* in dubbio si sceglie il limite basso */ return LIMITE_MOBILE; }
  return LIMITE_DESKTOP;
}

export function puoAvereMiniatura(file) {
  if (!file || !file.filename) return false;
  const ext = String(file.filename).split('.').pop()?.toLowerCase();
  if (!TIPI_MINIATURA.has(ext)) return false;
  return (file.size || 0) <= limiteMiniatura();
}

// ── cache condivisa ───────────────────────────────────────────────────────
// Map mantiene l'ordine di inserimento: la prima chiave e' la piu' vecchia,
// che e' esattamente quella da espellere.
const cache = new Map(); // id file -> blob URL
const inCorso = new Map(); // id file -> Promise, per non scaricare due volte

function ricorda(id, url) {
  if (cache.has(id)) { URL.revokeObjectURL(cache.get(id)); cache.delete(id); }
  cache.set(id, url);
  while (cache.size > MAX_IN_MEMORIA) {
    const piuVecchia = cache.keys().next().value;
    URL.revokeObjectURL(cache.get(piuVecchia));
    cache.delete(piuVecchia);
  }
}

/** Svuota tutto — chiamata quando si esce dal vault o si blocca la sessione. */
export function svuotaMiniature() {
  for (const url of cache.values()) URL.revokeObjectURL(url);
  cache.clear();
  inCorso.clear();
}

// ── TETTO ALLE DECIFRATURE IN PARALLELO ─────────────────────────────────────
// Aprendo una cartella con migliaia di foto, decine di righe entrano nello
// schermo insieme e chiederebbero la miniatura tutte in una volta. Ogni
// decifratura tiene in memoria il cifrato E il chiaro (picco ~2x il file):
// centinaia in parallelo bloccano la scheda. Qui non ne girano mai piu' di
// TRE; le altre aspettano in coda, pochi millisecondi. Non e' il caricamento
// pigro (quello lo fa gia' l'IntersectionObserver riga per riga): e' il freno
// che impedisce a un colpo di scroll di far partire tutto insieme.
const MAX_IN_PARALLELO = 3;
let inVolo = 0;
const coda = [];
function prendiPosto() {
  if (inVolo < MAX_IN_PARALLELO) { inVolo++; return Promise.resolve(); }
  return new Promise((risolvi) => coda.push(risolvi));
}
function lasciaPosto() {
  const prossima = coda.shift();
  if (prossima) prossima();     // il posto passa a chi era in coda, inVolo resta
  else inVolo--;                // nessuno aspetta: un posto si libera davvero
}

/**
 * Ottiene (o produce) il blob URL decifrato di un'immagine.
 * Condivisa con la vista a schermo intero, cosi' aprire una foto di cui si e'
 * gia' vista la miniatura e' istantaneo invece di riscaricarla.
 */
export async function urlImmagine(file, encKey, ancoraVoluto = () => true) {
  if (cache.has(file.id)) {
    // Rimettila in fondo: e' appena stata usata, non deve essere la prossima
    // a essere buttata via.
    const url = cache.get(file.id);
    cache.delete(file.id); cache.set(file.id, url);
    return url;
  }
  if (inCorso.has(file.id)) return inCorso.get(file.id);

  const lavoro = (async () => {
    await prendiPosto();
    try {
      // Il turno e' arrivato: se intanto la riga e' uscita dallo schermo (o il
      // componente e' morto), non si decifra niente. E' questo, non il solo
      // semaforo, che rende sano lo scroll su una cartella da 30.000 foto: i
      // turni delle righe non piu' guardate si saltano all'istante, e la
      // miniatura visibile non resta in coda dietro migliaia di lavori morti.
      if (!ancoraVoluto()) return null;

      // L'ANTEPRIMA, SE C'E' (2026-09-19). Sei kB invece del file intero: si
      // prova prima questa, e solo se manca o non si apre si torna alla
      // strada di sempre. Cosi' i file caricati prima del 19/09, che
      // anteprima non ce l'hanno, continuano a funzionare identici.
      if (file.anteprimaNome && file.anteprimaIv) {
        try {
          const { apiScaricaAnteprima } = await import('../utils/api');
          const { decryptBuffer } = await import('../utils/crypto');
          const cifrata = await apiScaricaAnteprima(file);
          const chiaro = await decryptBuffer(encKey, await cifrata.arrayBuffer(), file.anteprimaIv);
          const url = URL.createObjectURL(new Blob([chiaro], { type: 'image/webp' }));
          ricorda(file.id, url);
          return url;
        } catch { /* si prosegue col file intero */ }
      }

      const { apiDownloadFile, marcaVersione } = await import('../utils/api');
      const { blob, isEncrypted } = await apiDownloadFile(file.id, marcaVersione(file));
      let finale = blob;
      if (isEncrypted) {
        // Due formati convivono: v1 a IV singolo (decryptFile) e v2 a blocchi
        // (l'app, i file grandi). Il v2 si riconosce dalla RIGA
        // (formatVersion), esattamente come fa lo scaricamento in
        // useFiles.downloadFile — MAI dai primi byte: "LCB2" sono quattro byte
        // e un v1 puo' cominciare cosi' per caso. Cosi' un v1 prende il
        // percorso di sempre, riga per riga. Il download vero resta su
        // scaricaABlocchi: questa e' solo la lettura per la miniatura.
        if (Number(file.formatVersion) === 2) {
          const { decifraV2InMemoria } = await import('../utils/cifraturaBlocchi');
          finale = await decifraV2InMemoria(blob, encKey, file.mimeType || '');
        } else {
          const { decryptFile } = await import('../utils/crypto');
          const r = await decryptFile(encKey, blob, file.iv, file.encryptedName);
          finale = r.plainBlob;
        }
      }
      const url = URL.createObjectURL(finale);
      ricorda(file.id, url);
      return url;
    } finally {
      lasciaPosto();
    }
  })();

  inCorso.set(file.id, lavoro);
  try { return await lavoro; }
  finally { inCorso.delete(file.id); }
}

/**
 * La miniatura di una riga. Se non e' un'immagine idonea non renderizza
 * niente e lascia il posto all'icona: la decisione la prende chi chiama, con
 * puoAvereMiniatura().
 */
export default function ImageThumb({ file, encKey, size = 32, onOpen, fallback = null }) {
  const [url, setUrl] = useState(() => cache.get(file.id) || null);
  const [fallita, setFallita] = useState(false);
  const rif = useRef(null);

  useEffect(() => {
    if (url || fallita) return undefined;
    const nodo = rif.current;
    if (!nodo) return undefined;

    let vivo = true;
    let chiesto = false;
    let visibile = false;
    // Si osserva di continuo, entrata E uscita: non ci si disconnette al primo
    // ingresso, perche' serve sapere se la riga e' ANCORA sullo schermo quando
    // arriva il suo turno di decifratura (vedi urlImmagine, ancoraVoluto). La
    // richiesta si fa una volta sola (chiesto); se il turno la trova fuori
    // schermo si salta e chiesto torna libero, cosi' rientrando si richiede.
    // rootMargin 200px: si parte poco prima dell'ingresso vero, non mezzo
    // schermo, per non decifrare roba che non si guardera'.
    const osservatore = new IntersectionObserver((voci) => {
      visibile = voci.some((v) => v.isIntersecting);
      if (visibile && !chiesto) {
        chiesto = true;
        urlImmagine(file, encKey, () => vivo && visibile)
          .then((u) => { if (!vivo) return; if (u) setUrl(u); else chiesto = false; })
          .catch(() => { if (vivo) setFallita(true); });
      }
    }, { rootMargin: '200px' });

    osservatore.observe(nodo);
    return () => { vivo = false; osservatore.disconnect(); };
  }, [file, encKey, url, fallita]);

  // Nessuna revoca qui: il blob URL appartiene alla cache condivisa, non a
  // questo componente. Se lo revocasse allo smontaggio, la stessa immagine
  // aperta a schermo intero mostrerebbe un riquadro rotto.

  // Se la decifratura non riesce (formato imprevisto, file troncato, chiave
  // che non torna) non si lascia il vuoto e non si mostra un'immagine rotta:
  // si torna all'icona del file, come per qualunque non-immagine. Un difetto
  // nostro non si racconta all'utente come un danno ai suoi dati.
  if (fallita) return fallback;

  return (
    <div
      ref={rif}
      className={`vz-thumb ${url ? 'pronta' : 'in-attesa'}`}
      style={{ width: size, height: size }}
      onClick={onOpen ? (e) => { e.stopPropagation(); onOpen(file); } : undefined}
    >
      {/* onError: la decifratura puo' riuscire e i byte non essere
          un'immagine (un .jpg da 40 byte, un file rinominato). Senza questo
          il browser mostra l'icona di immagine rotta; con questo si torna
          all'icona del file, come per ogni altro errore. */}
      {url && <img src={url} alt="" loading="lazy" draggable={false} onError={() => setFallita(true)} />}
    </div>
  );
}
